import React from 'react';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import teamPhoto1 from './assets/Team/IMG_3784.jpeg';
import teamPhoto2 from './assets/Team/IMG_3794.jpg';

const TeamSection = () => {
  const teamMembers = [
    {
      id: 1,
      name: "Tomasz Chmiel",
      role: "Co-Founder & Lead Bioresonance Therapist",
      phone: "0776 137 4278",
      email: "info@bioresonance-leicester.co.uk",
      image: teamPhoto1,
      description: "Co-founder of Bioresonance Leicester since 2017, Tomasz is passionate about helping others through holistic methods. With years of education, hands-on experience, and continuous learning, he specializes in personalized bioresonance therapy tailored to each individual's unique needs.",
      qualifications: ["Certified BICOM Bioresonance Device Training", "Cupping Therapy Certification", "Swedish Massage Certification", "Mental Health Specialty Module (NCSCT)"]
    },
    {
      id: 2,
      name: "Pawel Sieczka",
      role: "Co-Founder & Senior Bioresonance Specialist",
      phone: "0792 382 5242",
      email: "info@bioresonance-leicester.co.uk",
      image: teamPhoto2,
      description: "Co-founder of Bioresonance Leicester since 2017, Pawel brings dedication and care to every client interaction. His extensive training includes specialist work in holistic diagnosis and support for autoimmune diseases, with regular participation in international bioresonance congresses.",
      qualifications: ["Certified BICOM Bioresonance Device Training", "Assessment of Core Knowledge and Practice Skills (NCSCT)", "Mental Health Specialty Module (NCSCT)", "Specialist Training in Autoimmune Diseases (Germany)"]
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            Meet Our Expert Team
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our dedicated team of certified bioresonance therapists is committed to providing you with the highest quality care and personalized treatment plans.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {teamMembers.map((member, index) => (
            <div 
              key={member.id} 
              className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-start gap-8`}
            >
              {/* Image - same photo card and hover effect as Contact page */}
              <div className="w-full lg:w-1/2 flex items-start">
                <div className="relative group w-full">
                  {/* Decorative background shapes */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                  
                  {/* Main image container with creative border */}
                  <div className="relative p-4">
                    <div className="relative overflow-hidden rounded-3xl">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-96 object-cover object-[center_20%] transform group-hover:scale-110 transition-all duration-500"
                      />
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Contact info overlay on hover (same as Contact page) */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 border border-gold/30">
                          <h4 className="text-white text-xl font-bold mb-2 text-center" style={{ fontFamily: "Playfair Display, serif", textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                            {member.name}
                          </h4>
                          <p className="text-gold text-sm font-semibold text-center border-t border-gold/50 pt-2" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                            {member.role}
                          </p>
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-center space-x-2">
                              <FaPhone className="text-gold text-sm" />
                              <a
                                href={`tel:${member.phone}`}
                                className="text-white hover:text-gold transition-colors duration-300 text-sm"
                              >
                                {member.phone}
                              </a>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                              <FaEnvelope className="text-gold text-sm" />
                              <a
                                href={`mailto:${member.email}`}
                                className="text-white hover:text-gold transition-colors duration-300 text-xs"
                              >
                                {member.email}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Creative border with gold accent */}
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                    <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                  </div>
                  
                  {/* Floating elements */}
                  <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                  <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2 space-y-6 flex flex-col justify-start">
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    {member.name}
                  </h3>
                  <p className="text-lg text-gold font-semibold mb-4">
                    {member.role}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {member.description}
                  </p>
                </div>

                {/* Decorative element */}
                <div className="w-16 h-1 bg-gradient-to-r from-gold to-yellow rounded-full"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gray-50 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
              Ready to Start Your Healing Journey?
            </h3>
            <p className="text-gray-600 mb-6">
              Our experienced founders Tomasz and Pawel are here to guide you through your bioresonance therapy journey.
              Contact us today to schedule your consultation and discover how we can help you achieve optimal health.
            </p>
            <a
              href="https://www.sumupbookings.com/t-p-wellness-ltd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
